import {noop, stableStringify} from '@sail/utils';
import PollingController from 'src/internal/revamp/polling/PollingController';

import type {WatchCleanupFn} from 'src/internal/revamp/cache/MemoryCache';
import type {GraphQlOperationErrors} from 'src/internal/revamp/graphql/types/errors';
import type ResultStore from 'src/internal/revamp/observable/ResultStore';
import type {
  Observer,
  ObserverNextValue,
} from 'src/internal/revamp/observable/types';
import type PollingClock from 'src/internal/revamp/polling/PollingClock';
import type {PollConfig} from 'src/internal/revamp/polling/types';
import type {
  DataOfDoc,
  ErrorExtensionsOfDoc,
  GraphQlDocument,
  VariablesOfDoc,
} from 'src/internal/shared/graphql/types/document';

export type Subscription = {
  unsubscribe: () => void;
};

export default abstract class ObservableData<
  TDocumentNode extends GraphQlDocument<any, any, any, any>,
> {
  // Document node that will be used to check apollo cache for data.
  protected query: TDocumentNode;

  // Variables given to the useQuery hook, used to check apollo cache for data.
  protected variables: VariablesOfDoc<TDocumentNode> | undefined;

  // List of observers for this particular document + variables combination.
  protected observers: Set<Observer<TDocumentNode>> = new Set();

  protected resultStore: ResultStore;

  protected latestResult: {
    data: DataOfDoc<TDocumentNode> | null;
    errors: GraphQlOperationErrors<ErrorExtensionsOfDoc<TDocumentNode>> | null;
    // Flag to indicate there is a network request in progress for this query.
    isPending: boolean;
  } = {data: null, errors: null, isPending: false};

  protected pollingController: PollingController<
    Observer<TDocumentNode>
  > | null = null;

  // Fake observer used to register poll config during suspense phase.
  // This allows retry config to be available before real observers subscribe.
  private suspenseObserver: Observer<TDocumentNode> | null = null;

  constructor(
    query: TDocumentNode,
    variables: VariablesOfDoc<TDocumentNode> | undefined,
    resultStore: ResultStore,
    pollingClock: PollingClock | null = null,
  ) {
    this.query = query;
    this.variables = variables;
    this.resultStore = resultStore;

    if (pollingClock) {
      this.pollingController = new PollingController(pollingClock, {
        isCurrentlyFetching: () => this.isCurrentlyFetching(),
        triggerRefetch: () => this.triggerRefetch(),
        retryFetch: () => this.retryFetch(),
      });
    }
  }

  protected notifyObservers(result: ObserverNextValue<TDocumentNode>) {
    this.observers.forEach(function triggerObserverNext(observer) {
      observer.next(result);
    });
  }

  protected notifyObserversWithCurrentResult() {
    this.notifyObservers({
      data: this.latestResult?.data || null,
      errors: this.latestResult?.errors || null,
    });
  }

  protected watch(): WatchCleanupFn {
    return this.resultStore.watchResults(
      this.query,
      this.variables,
      (results: {
        data: DataOfDoc<TDocumentNode> | null;
        errors: GraphQlOperationErrors<
          ErrorExtensionsOfDoc<TDocumentNode>
        > | null;
      }) => {
        this.notifyObservers(results);
      },
    );
  }

  public setIsPending(isPending: boolean) {
    if (this.latestResult?.isPending !== isPending) {
      this.latestResult = this.latestResult
        ? {
            data: this.latestResult.data,
            errors: this.latestResult.errors,
            isPending,
          }
        : {data: null, errors: null, isPending};
      this.notifyObserversWithCurrentResult();
    }
  }

  public getCurrentResult(): {
    data: DataOfDoc<TDocumentNode> | null;
    errors: GraphQlOperationErrors<ErrorExtensionsOfDoc<TDocumentNode>> | null;
    isPending: boolean;
  } {
    const newResult = this.resultStore.getCurrentResult(
      this.query,
      this.variables,
    );

    // Adapt the value for new results from the result store
    if (
      stableStringify(this.latestResult?.data) !==
        stableStringify(newResult.data) ||
      stableStringify(this.latestResult?.errors) !==
        stableStringify(newResult.errors)
    ) {
      this.latestResult = {
        data: newResult.data,
        errors: newResult.errors,
        isPending: this.latestResult?.isPending || false,
      };
    }

    // Return a stable reference if the results haven't changed.
    return this.latestResult;
  }

  public isActive(): boolean {
    return this.observers.size > 0;
  }

  protected registerPollConfig(
    observer: Observer<TDocumentNode>,
    pollConfig: PollConfig,
  ): void {
    this.pollingController?.registerConfig(observer, pollConfig);
  }

  protected unregisterPollConfig(observer: Observer<TDocumentNode>): void {
    this.pollingController?.unregisterConfig(observer);
  }

  protected registerSuspensePollConfig(pollConfig: PollConfig): void {
    if (!this.pollingController) {
      return;
    }

    // Create a fake observer if we don't have one yet
    if (!this.suspenseObserver) {
      this.suspenseObserver = {
        next: noop,
        error: noop,
      };
    }

    this.pollingController.registerConfig(this.suspenseObserver, pollConfig);
  }

  protected unregisterSuspensePollConfig(): void {
    if (this.suspenseObserver && this.pollingController) {
      this.pollingController.unregisterConfig(this.suspenseObserver);
      this.suspenseObserver = null;
    }
  }

  protected getEffectiveInterval(): number | null {
    return this.pollingController?.getEffectiveInterval() ?? null;
  }

  protected getEffectiveExpectedErrors(): string[] {
    return this.pollingController?.getEffectiveExpectedErrors() ?? [];
  }

  protected getEffectiveRetryConfig() {
    return this.pollingController?.getEffectiveRetryConfig() ?? null;
  }

  protected abstract isCurrentlyFetching(): boolean;

  protected abstract triggerRefetch(): void;

  protected abstract retryFetch(): void;

  public getPollingState(): {isRetrying: boolean; isStopped: boolean} | null {
    return this.pollingController?.getPollingState() ?? null;
  }

  protected resetPollTimer(): void {
    this.pollingController?.resetTimer();
  }

  protected handleFetchResultForPolling(
    errors: GraphQlOperationErrors<ErrorExtensionsOfDoc<TDocumentNode>> | null,
  ): void {
    if (!this.pollingController) {
      return;
    }

    const hasClientErrors = (errors?.clientErrors.length ?? 0) > 0;
    const hasUnexpectedErrors = errors
      ? this.hasUnexpectedErrors(errors)
      : false;

    this.pollingController.handleFetchResult({
      hasClientErrors,
      hasUnexpectedErrors,
    });
  }

  protected hasUnexpectedErrors(
    errors: GraphQlOperationErrors<ErrorExtensionsOfDoc<TDocumentNode>>,
  ): boolean {
    if (errors.networkErrors.length > 0) {
      return true;
    }

    const expectedErrors = this.getEffectiveExpectedErrors();

    for (const gqlError of errors.graphQlErrors) {
      const errorCode = gqlError.extensions?.error?.code;
      if (errorCode && !expectedErrors.includes(errorCode)) {
        return true;
      }
    }

    return false;
  }
}
